import React from "react";
import { useSessionStorage } from "@riotgames/browserstorage-rg-web";
import { RegistrationEvent } from "./constants";
import { sendRegistrationEvent } from "./sendRegistrationEvent";
/**
 * Tracks a user signing up or signin in via GA
 *
 */
export const useRegistrationEvents = ({ AUTH_IN_PROGRESS = "ares.auth_in_progress", REGISTRATION_METHOD = "ares.registration_method", }) => {
    const { getItem, setItem, removeItem } = useSessionStorage();
    /**
     * Tracks the beginning of a registration session.
     * This could be login or signup (RSOPrompt).
     * Remembers the registration method for the future.
     * Also remembers the fact that registration has started
     * and a confirmation event should be tracked.
     */
    const trackRegistrationStart = React.useCallback((method, event = RegistrationEvent.BUTTON_CLICK) => {
        sendRegistrationEvent({ event, registrationMethod: method });
        // Remember the registration method for future events.
        console.log("[GAREG] Remembering registration method", String(method));
        // Track that a registration using this method needs to be logged if it finishes.
        setItem(AUTH_IN_PROGRESS, "true");
        setItem(REGISTRATION_METHOD, method);
    }, [AUTH_IN_PROGRESS, REGISTRATION_METHOD, setItem]);
    /**
     * Track a generic registration event.
     * For example download or pbe download events.
     * Adds registration method information to tracked events.
     */
    const trackRegistrationEvent = React.useCallback(({ event, data }) => {
        try {
            const registrationMethod = getItem(REGISTRATION_METHOD) || "unidentified";
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            sendRegistrationEvent({ event: event, registrationMethod, ...data });
        }
        catch (e) {
            console.warn("GA Event Error Tracking Registration Event: " + e);
        }
    }, [REGISTRATION_METHOD, getItem]);
    /**
     * Track the end of a registration session (signup or login).
     * use with registrationConfirmation or pbeRegistrationConfirmation.
     * Will forget that a registration was initiated so this event
     * will be sent once per registration cycle.
     */
    const trackRegistrationConfirmation = React.useCallback(({ event = RegistrationEvent.CONFIRMATION, data } = {}) => {
        const registrationMethod = getItem(REGISTRATION_METHOD);
        const authInProgress = getItem(AUTH_IN_PROGRESS);
        if (!authInProgress || !registrationMethod) {
            // Either direct access or we have already sent a registration event.
            // TODO: We don't need a warning?
            console.warn("[GAREG] not tracking registration because no method is stored");
            return;
        }
        sendRegistrationEvent({ event: event, registrationMethod, ...data });
        // Forget the registration method after sending a confirmation.
        console.log("[GAREG] Removing auth_in_progress flag from session", String(registrationMethod));
        removeItem(AUTH_IN_PROGRESS);
    }, [AUTH_IN_PROGRESS, REGISTRATION_METHOD, getItem, removeItem]);
    const isRegistrationInProgress = function () {
        return !!getItem(AUTH_IN_PROGRESS);
    };
    return {
        trackRegistrationStart,
        trackRegistrationEvent,
        trackRegistrationConfirmation,
        isRegistrationInProgress,
    };
};
//# sourceMappingURL=useRegistrationEvents.js.map